import { api, firebase } from "common";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "components/alert/AlertContext";
import { LocationOn, Search, Close, MyLocation, History, DeleteOutline } from "@mui/icons-material";
import { Box, TextField, Paper, ListItemButton, ListItemText, ListItemIcon, Typography, CircularProgress, IconButton, InputAdornment, Button, Divider, List, Chip, Stack, Card, CardContent } from "@mui/material";

interface LocationSearchProps {
    open: boolean;
    onClose: () => void;
    placeholder?: string;
    type: 'pickup' | 'drop' | 'waypoint';
    onSelect: (location: ILocationResult) => void;
}

export interface ILocationResult {
    lat: number;
    lng: number;
    add: string;
    placeId?: string;
}

interface SearchRadiusOption {
    value: string;
    label: string;
    radius: number;
}

interface HistoryItem {
    lat: number;
    lng: number;
    add: string;
    placeId?: string;
    timestamp: number;
}

const HISTORY_KEY = 'location_history';

export const LocationSearch = ({
    open,
    type,
    onClose,
    onSelect,
    placeholder,
}: LocationSearchProps) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState<any[]>([]);
    const [coordsLoading, setCoordsLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [locationLoading, setLocationLoading] = useState(false);
    const [searchRadius, setSearchRadius] = useState<string>('near');
    const [showRadiusOptions, setShowRadiusOptions] = useState(false);
    const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

    const { fetchCoordsfromPlace } = api;

    useEffect(() => {
        try {
            const stored = localStorage.getItem(HISTORY_KEY);
            if (stored) {
                setHistory(JSON.parse(stored));
            }
        } catch (error) {
            console.error('Erro ao carregar histórico:', error);
        }
    }, []);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setUserLocation(location);
                    localStorage.setItem('gps', JSON.stringify(location));
                },
                () => {
                    const gpsLocation = JSON.parse(localStorage.getItem('gps') || '{}');
                    if (gpsLocation?.lat) {
                        setUserLocation(gpsLocation);
                    }
                }
            );
        } else {
            const gpsLocation = JSON.parse(localStorage.getItem('gps') || '{}');
            if (gpsLocation?.lat) {
                setUserLocation(gpsLocation);
            }
        }
    }, []);

    const saveHistory = (location: ILocationResult) => {
        try {
            const newItem: HistoryItem = {
                lat: location.lat,
                lng: location.lng,
                add: location.add,
                timestamp: Date.now(),
                placeId: location.placeId,
            };

            let updated = history.filter(item => item.add !== location.add);
            updated.unshift(newItem);

            if (updated.length > 10) {
                updated = updated.slice(0, 10);
            }

            setHistory(updated);
            localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
        } catch (error) {
            console.error('Erro ao salvar histórico:', error);
        }
    };

    const clearHistory = () => {
        setHistory([]);
        localStorage.removeItem(HISTORY_KEY);
    };

    const radiusOptions: SearchRadiusOption[] = [
        { value: 'near', label: t('NEAR'), radius: 2000 },
        { value: 'city', label: t('CITY'), radius: 25000 },
        { value: 'region', label: t('REGION'), radius: 100000 },
        { value: 'country', label: t('COUNTRY'), radius: 500000 },
    ];

    const buildGooglePlacesUrl = (text: string, radiusType: string) => {
        let url = 'https://maps.googleapis.com/maps/api/place/autocomplete/json';
        url += `?input=${encodeURIComponent(text)}`;

        const selectedOption = radiusOptions.find(opt => opt.value === radiusType);
        if (selectedOption) {
            url += `&radius=${selectedOption.radius}`;
        }

        const gpsLocation = userLocation || JSON.parse(localStorage.getItem('gps') || '{}');
        if (gpsLocation?.lat && radiusType === 'near') {
            url += `&location=${gpsLocation.lat},${gpsLocation.lng}`;
            url += `&rankby=distance`;
        } else if (gpsLocation?.lat) {
            url += `&location=${gpsLocation.lat},${gpsLocation.lng}`;
        }

        const settings = JSON.parse(localStorage.getItem('settings') || '{}');
        url += `&language=${settings?.mapLanguage || 'pt-BR'}`;

        return url;
    };

    const searchPlaces = async (text: string, radiusType: string = searchRadius) => {
        if (text.length < 3) {
            setResults([]);
            return;
        }

        setSearchLoading(true);

        try {
            const placesUrl = buildGooglePlacesUrl(text, radiusType);
            const data = await firebase.callFunction('googleapis-googleApisAutocomplete', { url: placesUrl });

            if (data.searchResults && data.searchResults.length > 0) {
                setResults(data.searchResults);
            } else {
                setResults([]);
                showAlert({
                    type: "INFO",
                    title: t("ADM_INFO"),
                    message: data.status || t("ADM_NO_RESULTS_FOUND"),
                    buttons: [{ text: t("ADM_OK"), style: 'primary' }]
                });
            }
        } catch (error) {
            console.error('Erro na busca:', error);
            showAlert({
                type: "ERROR",
                title: t("ADM_ERROR"),
                message: t("ADM_ERROR_SEARCHING_PLACES"),
                buttons: [{ text: t("ADM_OK"), style: 'primary' }]
            });
            setResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSelect = async (item: any) => {
        setCoordsLoading(true);

        try {
            const coords = await fetchCoordsfromPlace(item.place_id);

            if (coords?.lat) {
                const location: ILocationResult = {
                    lat: coords.lat,
                    lng: coords.lng,
                    add: item.description,
                    placeId: item.place_id,
                };
                saveHistory(location);
                onSelect(location);
                onClose();
            } else {
                showAlert({
                    type: "ERROR",
                    title: t("ADM_ERROR"),
                    message: t("ADM_PLACE_TO_COORDS_ERROR"),
                    buttons: [{ text: t("ADM_OK"), style: 'primary' }]
                });
            }
        } catch (error) {
            console.error('Erro ao buscar coordenadas:', error);
            showAlert({
                type: "ERROR",
                title: t("ADM_ERROR"),
                message: t("ADM_PLACE_TO_COORDS_ERROR"),
                buttons: [{ text: t("ADM_OK"), style: 'primary' }]
            });
        } finally {
            setCoordsLoading(false);
        }
    };

    const handleHistorySelect = (item: HistoryItem) => {
        const location: ILocationResult = {
            lat: item.lat,
            lng: item.lng,
            add: item.add,
            placeId: item.placeId,
        };
        onSelect(location);
        onClose();
    };

    const handleUseCurrentLocation = () => {
        if (navigator.geolocation) {
            setLocationLoading(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location: ILocationResult = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                        add: t("ADM_MY_LOCATION"),
                    };
                    const gpsLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setUserLocation(gpsLocation);
                    localStorage.setItem('gps', JSON.stringify(gpsLocation));
                    saveHistory(location);
                    onSelect(location);
                    onClose();
                    setLocationLoading(false);
                },
                () => {
                    showAlert({
                        type: "ERROR",
                        title: t("ADM_ERROR"),
                        message: t("ADM_LOCATION_NOT_AVAILABLE"),
                        buttons: [{ text: t("ADM_OK"), style: 'primary' }]
                    });
                    setLocationLoading(false);
                }
            );
        } else {
            showAlert({
                type: "ERROR",
                title: t("ADM_ERROR"),
                message: t("ADM_GEOLOCATION_NOT_SUPPORTED"),
                buttons: [{ text: t("ADM_OK"), style: 'primary' }]
            });
        }
    };
    const getSelectedRadiusLabel = () => {
        const selected = radiusOptions.find(opt => opt.value === searchRadius);
        return selected ? selected.label : t('NEAR');
    };

    const handleSearch = () => {
        if (searchTerm.length > 2) {
            searchPlaces(searchTerm);
        }
    };

    if (!open) return null;

    return (
        <Box
            onClick={onClose}
            sx={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
                display: 'flex',
                position: 'fixed',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(5px)',

            }}
        >
            <Card
                sx={{
                    p: 0,
                    maxWidth: 600,
                    width: '100%',
                    display: 'flex',
                    borderRadius: 4,
                    maxHeight: '90dvh',
                    overflow: 'hidden',
                    border: "3px solid",
                    borderColor: "divider",
                    flexDirection: 'column',
                    bgcolor: 'background.default',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <Box
                    sx={{
                        justifyContent: 'space-between',
                        borderBottom: '3px solid',
                        borderColor: 'divider',
                        alignItems: 'center',
                        display: 'flex',
                        p: 3,
                    }}
                >
                    <Typography variant="h6" fontWeight={600}>
                        {type === 'pickup' ? t('ADM_SELECT_PICKUP') :
                            type === 'drop' ? t('ADM_SELECT_DROP') :
                                t('ADM_SELECT_WAYPOINT')}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>

                <CardContent sx={{ p: 0, overflowY: 'auto', flex: 1 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        value={searchTerm}
                        placeholder={placeholder || t("ADM_SEARCH_FOR_ADDRESS")}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            if (e.target.value.length < 3) {
                                setResults([]);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && searchTerm.length > 2) {
                                handleSearch();
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LocationOn color="primary" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {locationLoading ? (
                                        <CircularProgress size={20} />
                                    ) : searchTerm ? (
                                        <IconButton size="small" onClick={() => {
                                            setSearchTerm('');
                                            setResults([]);
                                        }}>
                                            <Close />
                                        </IconButton>
                                    ) : (
                                        <IconButton size="small" onClick={handleUseCurrentLocation}>
                                            <MyLocation />
                                        </IconButton>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ mb: 2, px: 2 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            sx={{
                                justifyContent: 'space-between',
                                borderRadius: 2,
                                py: 1.5,
                                borderColor: showRadiusOptions ? 'primary.main' : 'divider',
                                borderWidth: showRadiusOptions ? 2 : 1,
                            }}
                            endIcon={showRadiusOptions ? <Close /> : <Search />}
                            onClick={() => setShowRadiusOptions(!showRadiusOptions)}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <LocationOn fontSize="small" color="primary" />
                                <Typography variant="body2" fontWeight={500}>
                                    {t('SEARCH_RADIUS')}: {getSelectedRadiusLabel()}
                                </Typography>
                                {userLocation && (
                                    <Chip
                                        size="small"
                                        color="primary"
                                        label={t('NEAR')}
                                        variant="outlined"
                                        sx={{ ml: 1, height: 20, fontSize: '0.625rem' }}
                                    />
                                )}
                            </Box>
                        </Button>

                        {showRadiusOptions && (
                            <Paper
                                elevation={2}
                                sx={{
                                    mt: 1,
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                    border: '3px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Box sx={{ p: 1 }}>
                                    {radiusOptions.map((option) => (
                                        <ListItemButton
                                            key={option.value}
                                            selected={searchRadius === option.value}
                                            onClick={() => {
                                                setSearchRadius(option.value);
                                                setShowRadiusOptions(false);
                                            }}
                                            sx={{
                                                borderRadius: 1.5,
                                                mb: 0.5,
                                                '&:last-child': { mb: 0 },
                                                py: 1.2,
                                                bgcolor: searchRadius === option.value ? 'primary.light' : 'transparent',
                                                '&:hover': {
                                                    bgcolor: searchRadius === option.value ? 'primary.light' : 'action.hover',
                                                },
                                            }}
                                        >
                                            <ListItemIcon>
                                                <LocationOn
                                                    color={searchRadius === option.value ? 'primary' : 'disabled'}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={option.label}
                                                primaryTypographyProps={{
                                                    fontWeight: searchRadius === option.value ? 600 : 400,
                                                    color: searchRadius === option.value ? 'primary.main' : 'text.primary',
                                                }}
                                            />
                                            {searchRadius === option.value && (
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: 'primary.main',
                                                        ml: 1,
                                                    }}
                                                />
                                            )}
                                        </ListItemButton>
                                    ))}
                                </Box>
                            </Paper>
                        )}
                    </Box>

                    <Divider />

                    <Box sx={{ flex: 1, }}>
                        {searchLoading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                                <CircularProgress size={30} />
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                    {t("SEARCHING")}
                                </Typography>
                            </Box>
                        ) : coordsLoading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                                <CircularProgress size={30} />
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                    {t("SEARCHING")} - {t("coordinates")}
                                </Typography>
                            </Box>
                        ) : results.length > 0 ? (
                            <List sx={{ maxHeight: 300, overflow: 'auto', px: 1, pb: 2 }}>
                                {results.map((item: any, index: number) => (
                                    <ListItemButton
                                        key={index}
                                        disabled={coordsLoading}
                                        onClick={() => handleSelect(item)}
                                        divider={index < results.length - 1}
                                        sx={{
                                            p: 1,
                                            mb: 1,
                                            gap: 2,
                                            borderWidth: 0,
                                            borderRadius: 1,
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                            }
                                        }}
                                    >
                                        <LocationOn color="primary" />

                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" fontWeight={500}>
                                                    {item.structured_formatting?.main_text || item.description?.split(',')[0]}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {item.structured_formatting?.secondary_text || item.description}
                                                </Typography>
                                            }
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        ) : (
                            <Box py={4} textAlign="center">
                                <Typography color="text.secondary">
                                    {searchTerm.length > 2
                                        ? t("ADM_NO_RESULTS_FOUND")
                                        : t("ADM_SEARCH_FOR_ADDRESS")}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {history.length > 0 && (
                        <Box p={2} pb={0} borderTop="3px solid" borderColor="divider">
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography variant="subtitle2" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                    <History fontSize="small" />
                                    {t('LOCATION_HISTORY')}
                                </Typography>
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={clearHistory}
                                    startIcon={<DeleteOutline />}
                                    sx={{ borderRadius: 2 }}
                                >
                                    {t('TO_CLEAN')}
                                </Button>
                            </Box>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    overflowX: 'auto',
                                    flexWrap: 'nowrap',
                                    '&::-webkit-scrollbar': {
                                        height: 6,
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        bgcolor: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        bgcolor: 'divider',
                                        borderRadius: 3,
                                    },
                                }}
                            >
                                {history.map((item, index) => (
                                    <Chip
                                        key={index}
                                        label={item.add}
                                        variant="outlined"
                                        icon={<History fontSize="small" />}
                                        onClick={() => handleHistorySelect(item)}
                                        sx={{
                                            maxWidth: 200,
                                            flexShrink: 0,
                                            borderRadius: 2,
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                            },
                                            '& .MuiChip-label': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};